import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { PhysicsBreakSpeed, PhysicsFriction, PhysicsMass, PhysicsRestitution } from "../physics/physics_masses.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { PhysicsCube } from "./physics_cube.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { GameScreen } from "../ui/screens/game_screen/game_screen.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";

export class JamJar extends BreakablePhysicsEntity implements LevelEntity
{
  public static readonly MIN_BREAK_IMPACT_SPEED = PhysicsBreakSpeed.glass_jar;
  public static readonly DROPPED_SPLASH_FORCE = 0.5;
  public static readonly DROPPED_GLASS_FORCE = 0.75;

  private static readonly DISC_AXIS_Z = Math.SQRT1_2;
  private static readonly DISC_AXIS_X = Math.SQRT1_2;

  private static readonly DISC_SIDES = 12;

  private static readonly MODELS: Record<number, ResourceID> = {
    1: ResourceID.Jam1x1Model,
    2: ResourceID.Jam1x2Model,
    3: ResourceID.Jam1x3Model,
  };

  private static readonly TEXTURES: readonly ResourceID[] = [
    ResourceID.JarCherryTexture,
    ResourceID.JarPlumTexture,
    ResourceID.JarLemonTexture,
    ResourceID.JarFigTexture
  ];

  private static readonly JAM_COLORS: readonly number[] = [
    0xb01040, // cherry
    0xf000c0, // plum
    0xffd040, // lemon
    0x80d000  // fig
  ];

  // A block occupies exactly one of these two axes (1-3), never both — the
  // model only exists standing up, so a "wide" piece (a jar tipped on its
  // side) is the same model/texture rotated -90° (see createMesh). Matches
  // PhysicsCube.createAuthoredMesh's `width > height` rotation convention.
  public readonly width_blocks: number;
  public readonly height_blocks: number;
  public readonly jam_set_index: number;
  private static readonly material_cache = new Map<number, THREE.MeshPhongMaterial>();
  private static readonly prepared_geometries =
    new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();

  public readonly kind = EntityKind.Jar;

  private readonly jam_color: THREE.Color;

  constructor(
    position: THREE.Vector3,
    width_blocks = 1,
    height_blocks = 1,
    jam_set_index?: number,
    lying = false,
    rotation?: { x?: number; y?: number; z?: number },
    surface_tilt_rad = 0
  )
  {
    const blocks = THREE.MathUtils.clamp( Math.round( Math.max( width_blocks, height_blocks ) ), 1, 3 );
    const horizontal = width_blocks > height_blocks;
    const set_index = JamJar.getSetIndex( jam_set_index );

    const mesh = JamJar.createMesh( blocks, horizontal, set_index, lying );

    super( mesh, position, {
      angular_damping: 0.4,
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
      collider_desc: JamJar.createColliderDesc( blocks, horizontal, lying, rotation ),
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      friction: PhysicsFriction.glass,
      linear_damping: 0.05,
      mass: PhysicsMass.glass_jar_per_cell * blocks,
      restitution: PhysicsRestitution.glass,
      arm_delay: 0.5,
    } );

    this.width_blocks = horizontal ? blocks : 1;
    this.height_blocks = horizontal ? 1 : blocks;
    this.jam_set_index = set_index;
    this.jam_color = new THREE.Color( JamJar.JAM_COLORS[set_index] );
  }

  private static getSetIndex( jam_set_index?: number ): number
  {
    return jam_set_index !== undefined
      ? ( ( jam_set_index % JamJar.TEXTURES.length ) + JamJar.TEXTURES.length ) % JamJar.TEXTURES.length
      : Math.floor( Math.random() * JamJar.TEXTURES.length );
  }

  private static createMesh( blocks: number, horizontal: boolean, set_index: number, lying: boolean ): THREE.Object3D
  {
    const model_id = JamJar.MODELS[blocks];
    const visual = ResourceManager.getModel( model_id );
    const material = JamJar.getJarMaterial( set_index );

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      child.castShadow = true;
      child.receiveShadow = false;
      child.geometry = JamJar.prepareGeometry( child.geometry );
      child.material = material;
    } );

    // The three jar FBX variants are authored standing up, label wrapped a
    // full 360° around the body (see prepareGeometry's cylindrical UV unwrap)
    // — a horizontal (width > height) piece is the same jar rotated -90° onto
    // its side, same convention as PhysicsCube.createAuthoredMesh.
    if ( horizontal )
      visual.rotation.z = -Math.PI * 0.5;

    visual.updateMatrixWorld( true );

    const base_size = new THREE.Box3().setFromObject( visual ).getSize( new THREE.Vector3() );
    const target_size = PhysicsCube.getSize();
    if ( base_size.x <= 0 || base_size.y <= 0 || base_size.z <= 0 )
      throw new Error( `Jam jar model ${model_id} has invalid dimensions` );

    if ( horizontal )
    {
      const base_radial = Math.max( base_size.y, base_size.z );
      const radial_scale = Math.min( target_size.y, target_size.z ) / base_radial;
      visual.scale.set( target_size.x * blocks / base_size.x, radial_scale, radial_scale );
    }
    else
    {
      const base_radial = Math.max( base_size.x, base_size.z );
      const radial_scale = Math.min( target_size.x, target_size.z ) / base_radial;
      visual.scale.set( radial_scale, target_size.y * blocks / base_size.y, radial_scale );
    }

    // The new FBX pivot is not at the geometric centre. Keep physics at the
    // rigid-body centre and offset only the rendered child to match it.
    visual.updateMatrixWorld( true );
    const center = new THREE.Box3().setFromObject( visual ).getCenter( new THREE.Vector3() );
    visual.position.sub( center );

    const group = new THREE.Group();
    group.add( visual );

    if ( lying ) group.rotation.x = Math.PI / 2;

    const root = new THREE.Group();
    root.add( group );
    return root;
  }

  private static createColliderDesc(
    blocks: number,
    horizontal: boolean,
    lying: boolean,
    rotation?: { x?: number; y?: number; z?: number }
  ): RAPIER.ColliderDesc | undefined
  {
    const target_size = PhysicsCube.getSize();

    const half_height = horizontal
      ? target_size.x * blocks / 2
      : target_size.y * blocks / 2;
    const radius = ( horizontal
      ? Math.min( target_size.y, target_size.z )
      : Math.min( target_size.x, target_size.z ) ) / 2;

    const visual_rotation = new THREE.Quaternion().setFromEuler(
      new THREE.Euler( 0, 0, horizontal ? -Math.PI * 0.5 : 0 )
    );
    const group_rotation = new THREE.Quaternion().setFromEuler(
      new THREE.Euler( lying ? Math.PI / 2 : 0, 0, 0 )
    );
    const mesh_rotation = group_rotation.multiply( visual_rotation );

    const body_rotation = new THREE.Quaternion().setFromEuler(
      new THREE.Euler( rotation?.x ?? 0, rotation?.y ?? 0, rotation?.z ?? 0, "XYZ" )
    );
    const axis = new THREE.Vector3( 0, 1, 0 )
      .applyQuaternion( mesh_rotation )
      .applyQuaternion( body_rotation );

    if ( Math.abs( axis.z ) < JamJar.DISC_AXIS_Z && Math.abs( axis.x ) < JamJar.DISC_AXIS_X )
      return undefined;

    const desc = RAPIER.ColliderDesc.cylinder( half_height, radius );

    return desc.setRotation( { x: mesh_rotation.x, y: mesh_rotation.y, z: mesh_rotation.z, w: mesh_rotation.w } );
  }

  private static prismPoints( half_height: number, radius: number ): Float32Array
  {
    const sides = JamJar.DISC_SIDES;
    const step = Math.PI * 2 / sides;

    const base = Math.PI / 2 - ( Math.floor( sides / 4 ) + 0.5 ) * step;
    const corner_radius = radius / Math.cos( Math.PI / sides );

    const points = new Float32Array( sides * 6 );
    let at = 0;

    for ( let i = 0; i < sides; i++ )
    {
      const x = Math.cos( base + i * step ) * corner_radius;
      const z = Math.sin( base + i * step ) * corner_radius;

      points[at++] = x; points[at++] = -half_height; points[at++] = z;
      points[at++] = x; points[at++] = half_height; points[at++] = z;
    }

    return points;
  }

  private static getJarMaterial(
    set_index: number
  ): THREE.MeshPhongMaterial
  {
    const cached = this.material_cache.get( set_index );
    if ( cached ) return cached;

    const texture = ResourceManager.getTexture( JamJar.TEXTURES[set_index] );
    const is_lemon = set_index === 2;
    const material = new THREE.MeshPhongMaterial( {
      map: texture,
      color: 0xffffff,
      emissive: 0xffffff,
      emissiveMap: texture,
      emissiveIntensity: is_lemon ? 0.25 : 0.30,
      shininess: 70,
      specular: new THREE.Color( 0x999999 ),

    } );

    this.material_cache.set( set_index, material );
    return material;
  }

  private static prepareGeometry(
    source: THREE.BufferGeometry
  ): THREE.BufferGeometry
  {
    const cached = this.prepared_geometries.get( source );
    if ( cached )
      return cached;

    if ( source.attributes.uv )
    {
      this.prepared_geometries.set( source, source );
      return source;
    }

    let indexed = source;
    if ( !indexed.index )
      indexed = mergeVertices( indexed );

    indexed.computeVertexNormals();

    const geometry = indexed.toNonIndexed();

    const pos = geometry.attributes.position;

    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const min_y = bb.min.y;
    const height = bb.max.y - min_y;
    if ( height <= 0 )
      throw new Error( "Cannot generate UVs for a flat jam jar geometry" );

    const uvs = new Float32Array( pos.count * 2 );

    for ( let i = 0; i < pos.count; i++ )
    {
      uvs[i * 2]     = Math.atan2( pos.getZ( i ), pos.getX( i ) ) / ( Math.PI * 2 ) + 0.5;
      uvs[i * 2 + 1] = ( pos.getY( i ) - min_y ) / height;
    }

    for ( let i = 0; i < pos.count; i += 3 )
    {
      const u0 = uvs[i * 2];
      const u1 = uvs[( i + 1 ) * 2];
      const u2 = uvs[( i + 2 ) * 2];

      const max_u = Math.max( u0, u1, u2 );
      const min_u = Math.min( u0, u1, u2 );

      if ( max_u - min_u > 0.5 )
      {
        for ( let k = 0; k < 3; k++ )
        {
          if ( uvs[( i + k ) * 2] < 0.5 )
            uvs[( i + k ) * 2] += 1.0;
        }
      }
    }

    geometry.setAttribute( "uv", new THREE.BufferAttribute( uvs, 2 ) );

    this.prepared_geometries.set( source, geometry );

    return geometry;
  }

  public getJamColor(): THREE.Color
  {
    return this.jam_color;
  }

  /** Splash-effect size scale — the jar's magnitude (1-3) regardless of orientation. */
  public getHeightBlocks(): number
  {
    return Math.max( this.width_blocks, this.height_blocks );
  }

  public playHitSound(): void
  {
    void audioController.playSound( AudioClips.GLASS_CUBE_HIT );
  }

  public canBeHit(): boolean
  {
    return this.isAlive();
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    this.shatter( false );
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    this.shatter( false );
  }

  public shatter( dropped: boolean ): void
  {
    const position = this.getPosition();
    const rotation = this.mesh.quaternion.clone();

    if ( dropped )
      this.playDropSound();
    else
      this.playHitSound();

    this.destroy();

    Vfx3DManager.addJamSplash(
      position,
      this.size.y * 0.5,
      this.getHeightBlocks(),
      this.jam_set_index,
      dropped ? JamJar.DROPPED_SPLASH_FORCE : 1,
      dropped ? JamJar.DROPPED_GLASS_FORCE : 1,
      0.0,
      rotation,
    );

    if ( dropped )
    {
      Vfx3DManager.addJamSpill(
        position,
        this.size.y * 0.5,
        this.getHeightBlocks(),
        position.y - this.size.y * 0.5,
        this.jam_set_index,
        rotation,
      );
    }
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public checkSelfDestruct(): void
  {
    if ( !this.isAlive() )
      return;

    const bottom_y = this.getPosition().y - this.halfHeight();
    const hit_floor = bottom_y < 0.2;

    if ( hit_floor || ( this.isArmed() && this.getImpactSpeed() >= JamJar.MIN_BREAK_IMPACT_SPEED ) )
      this.shatter( true );
  }

  public playDropSound(): void
  {
    const clip = Math.random() < 0.5
      ? AudioClips.GLASS_CUBE_DROP_1
      : AudioClips.GLASS_CUBE_DROP_2;

    void audioController.playSound( clip );
  }

  public destroy(): void
  {
    this.markDestroyed();
  }
}
